<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import type { Resolved, Service } from '@/types'
import { settings } from '@/content'
import { useContentHtml } from '@/lib/pageHtml'
import { baseHead, serviceLd, breadcrumbLd, faqLd } from '@/lib/seo'
import BreadcrumbBar from '@/components/BreadcrumbBar.vue'
import FaqAccordion from '@/components/FaqAccordion.vue'
import CtaProjet from '@/components/CtaProjet.vue'

const props = defineProps<{ resolved: Resolved }>()
const zone = computed(() => props.resolved.service as Service)

const contentHtml = useContentHtml(zone.value.path)
const crumbs = computed(() => [
  { label: 'Accueil', path: '/' },
  { label: zone.value.title, path: zone.value.path },
])

useHead(() => {
  const z = zone.value
  const scripts = [serviceLd(z), breadcrumbLd(crumbs.value)]
  if (z.faq?.length) scripts.push(faqLd(z.faq))
  return {
    ...baseHead({
      title: z.seo?.seo_title || z.title,
      description: z.seo?.seo_description || z.intro,
      canonical: z.seo?.canonical || z.path,
      index: z.seo?.robots_index !== false,
      image: z.seo?.og_image || z.featured_image || '',
    }),
    script: scripts,
  }
})
</script>

<template>
  <div class="zone">
    <!-- Hero -->
    <section class="zhero">
      <div class="container zhero__in">
        <span class="eyebrow">Zone d'intervention</span>
        <h1>{{ zone.h1 || zone.title }}</h1>
        <p v-if="zone.intro" class="zhero__sub">{{ zone.intro }}</p>
        <router-link to="/contact-2/" class="btn btn--primary">
          Demander une étude gratuite
        </router-link>
      </div>
    </section>

    <BreadcrumbBar :crumbs="crumbs" />

    <!-- Corps editorial (liste de services + presentation) -->
    <section class="section content">
      <div class="container">
        <div class="prose" v-html="contentHtml"></div>
      </div>
    </section>

    <!-- FAQ eventuelle -->
    <FaqAccordion v-if="zone.faq?.length" :items="zone.faq" :title="`FAQ : ${zone.title}`" />

    <!-- Contact agence de reference -->
    <section class="section--tight agencies">
      <div class="container">
        <span class="eyebrow">Nous contacter</span>
        <h2>Vos agences pour {{ zone.title }}</h2>
        <div class="agencies__grid">
          <div v-for="a in settings.agencies" :key="a.city" class="agencies__card">
            <h3>{{ a.name }} - {{ a.city }}</h3>
            <address class="agencies__addr">
              {{ a.street }}<br />
              {{ a.zip }} {{ a.town }}<br />
              <a :href="`tel:${a.phone_tel}`">{{ a.phone_display }}</a>
            </address>
            <p class="agencies__hours">{{ a.hours }}</p>
          </div>
        </div>
      </div>
    </section>

    <CtaProjet :subject="zone.title" />
  </div>
</template>

<style scoped>
.zhero {
  background: var(--bg-alt);
  border-bottom: 1px solid var(--border);
}
.zhero__in {
  padding: 56px 20px;
  max-width: 900px;
}
.zhero__in h1 {
  margin: 8px 0 14px;
  color: var(--green-dark);
}
.zhero__sub {
  color: var(--ink);
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 24px;
  max-width: 760px;
}

.agencies {
  background: var(--bg-alt2);
  border-top: 1px solid var(--border);
}
.agencies__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 18px;
}
.agencies__card {
  background: #fff;
  border: 1px solid var(--border);
  border-top: 3px solid var(--green);
  border-radius: var(--radius);
  padding: 22px;
  box-shadow: var(--shadow-sm);
}
.agencies__card h3 {
  color: var(--green-dark);
  font-size: 1.05rem;
}
.agencies__addr {
  font-style: normal;
  line-height: 1.8;
  color: var(--ink);
}
.agencies__addr a {
  color: var(--green-dark);
  font-weight: 700;
}
.agencies__hours {
  margin: 10px 0 0;
  color: var(--muted);
  font-size: 0.9rem;
}
@media (max-width: 640px) {
  .zhero__in {
    padding: 36px 20px;
  }
  .agencies__grid {
    grid-template-columns: 1fr;
  }
}
</style>
